import classnames from 'classnames';
import { useStaticQuery, graphql, navigate } from 'gatsby';
import React, { useState } from 'react';

import useResponsive from '../../hooks/useResponsive';
import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
} from '../UI/Dropdown/Dropdown';
import Emphasise from '../UI/Emphasise/Emphasise';
import Icon from '../UI/Icon/Icon';
import Link from '../UI/Link/Link';
import NavList from '../UI/NavList/NavList';
import SearchBar from '../UI/SearchBar/SearchBar';
import { AnimatePresence, motion } from '../UI/framer-motion-custom';
import * as styles from './ArticleNav.module.scss';

const CATEGORY_QUERY = graphql`
  query ArticleCategoryQuery {
    blogCategories: allWpCategory(
      filter: { count: { gt: 0 } }
      sort: { name: ASC }
    ) {
      nodes {
        uri
        name
        id
      }
    }

    newsCategories: allWpNewsCategory(
      filter: { count: { gt: 0 } }
      sort: { name: ASC }
    ) {
      nodes {
        uri
        name
        id
      }
    }
  }
`;

const ArticleNav = ({ isNews = false }) => {
  const { blogCategories, newsCategories } = useStaticQuery(CATEGORY_QUERY);
  const { isXLarge } = useResponsive();
  const [ searchVisible, setSearchVisible ] = useState(false);
  const categories = isNews ? newsCategories : blogCategories;
  const landingLink = !isNews ? '/blog/' : '/news/';

  function handleSearchBtnClick() {
    setSearchVisible(!searchVisible);
  }

  function handleSearchSubmit(value) {
    navigate(`${landingLink}search?q=${value}`);
  }

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        <h1 className={styles.heading}>
          <Link to={landingLink}>
            <Emphasise>
              {!isNews ? `The **Prime Health** Journal` : `**Prime Health** News`}
            </Emphasise>
          </Link>
        </h1>

        {isXLarge && (
          <ul className={styles.nav}>
            <li>
              <Link to={landingLink} activeClassName={styles.navActive}>
                All
              </Link>
            </li>
            {categories?.nodes?.map(({ id, name, uri }) => (
              <li key={id}>
                <Link to={uri} activeClassName={styles.navActive}>
                  {name}
                </Link>
              </li>
            ))}
          </ul>
        )}

        <div className={styles.controls}>
          {!isXLarge && (
            <div className={styles.dropdown}>
              <Dropdown>
                <DropdownButton className={styles.toggle}>
                  <Icon name="menu" /> Articles by category
                </DropdownButton>
                <DropdownMenu align="bottom-left" isFixed>
                  <NavList
                    nodes={categories?.nodes?.map(({ id, name, uri }) => ({
                      label: name,
                      path: uri,
                      id,
                    }))}
                    isPrimary
                    isReverse
                  />
                </DropdownMenu>
              </Dropdown>
            </div>
          )}

          <button
            className={styles.button}
            aria-label="Search articles"
            onClick={handleSearchBtnClick}
          >
            <Icon name="search" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {searchVisible && (
          <SearchBar
            as={motion.div}
            initial="close"
            animate="open"
            exit="close"
            key="test"
            variants={{
              open: {
                height: isXLarge ? 80 : 50,
              },
              close: {
                height: 0,
              },
            }}
            transition={{ duration: 0.4 }}
            onSubmit={handleSearchSubmit}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ArticleNav;
